<template>
  <ion-app>
    <ion-router-outlet ref="rootElementRef" />
    <LoginModalLayout
      v-if="loginModalIsOpen"
      v-model:is-open="loginModalIsOpen"
      @login="onLogin"
    />
    <AppShiftModal v-model:is-open="shiftModalIsOpen" />
    <OrderModalLayout
      v-if="rootElementRef"
      :order-id="orderModalActiveOrderId"
      :object-id="orderModalActiveObjectId"
      :is-open="orderModalIsOpen"
      :presenting-element="rootElementRef.$el"
      @dismiss="onOrderModalDismiss"
      @update="onOrderModalUpdate"
    />
  </ion-app>
</template>

<script setup lang="ts">
import {
  IonApp,
  IonRouterOutlet,
  useIonRouter,
  toastController,
} from '@ionic/vue'
import { useRoute } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { ref, computed, watch, onMounted, onDeactivated, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import useAppEventBus from '~/composables/useAppEventBus'
import usePreferredColorScheme from '@restify/packages/composables/usePreferredColorScheme'
import useAppUpdate from '@restify/packages/composables/useAppUpdate'
import { useProvideAppUIState } from '@restify/packages/composables/useAppUiState'
import { useProvideMoveItemsState } from '@restify/packages/composables/useMoveItemsState'
import { useShiftsLogic } from '@restify/packages/composables/useShiftsLogic'
import { reloadStoredEntities as HelpersAppReloadStoredEntities } from '@restify/packages/helpers/app'
import {
  addEventListener as HelpersVisibilityAddEventListener,
  removeEventListener as HelpersVisibilityRemoveEventListener,
} from '@restify/packages/helpers/visibility'
import AppShiftModal from '~/components/AppShiftModal.vue'
import useFeathers from '~/composables/useFeathers'
import useStores from '~/composables/useStores'
import LoginModalLayout from '~/views/LoginModalLayout.vue'
import OrderModalLayout from '~/views/OrderModalLayout.vue'
import { useAuthStore } from '~/stores/auth'
import { useAppStore } from '~/stores/app'
import packageJson from '../package.json'
import type { ExtractEmits } from '@restify/packages/types'

const { io } = useFeathers()
const { t } = useI18n()
const { apps: AppsStore } = useStores()
const AppStore = useAppStore()
const AuthStore = useAuthStore()
const router = useIonRouter()
const route = useRoute()
const { shiftShouldBeStarted, shiftsSupported, shift, shiftIsHappening } =
  useShiftsLogic(AuthStore)
const appBus = useAppEventBus()

io.on('disconnect', function () {
  AppStore.setNetworkConnectionStatus('reconnecting')
  appBus.emit('app-hidden')
})

io.on('reconnecting', function () {
  AppStore.setNetworkConnectionStatus('reconnecting')
})

io.on('connect', function () {
  AppStore.setNetworkConnectionStatus('connected')
  scheduleAppVisible()
})

// On iOS wake-from-sleep two triggers race to fire `app-visible`:
//   1. document `visibilitychange` (fires the moment JS resumes)
//   2. socket.io reconnect + feathers `jwt` reauth (~1-3 s later)
// Every subscriber to `app-visible` refetches over the socket — but
// the visibility-triggered burst goes out BEFORE the socket has
// reauthed, so half the reqs come back NotAuthenticated. Socket
// reconnects a beat later, retriggers `app-visible`, the retry
// succeeds — but a "NotAuthenticated" toast lingers on screen for
// 3 s in the meantime.
//
// `scheduleAppVisible` collapses both triggers into a single emit
// once the socket is actually live: if we're not connected yet,
// don't emit — the `io.on('connect')` handler above will call us
// again once reauth has run. 150 ms debounce also absorbs
// duplicate visibility events some webviews fire.
let appVisibleTimer: ReturnType<typeof setTimeout> | null = null
const scheduleAppVisible = () => {
  if (appVisibleTimer) clearTimeout(appVisibleTimer)
  appVisibleTimer = setTimeout(() => {
    appVisibleTimer = null
    if (!io.connected) return
    appBus.emit('app-visible')
  }, 150)
}

const onVisibilityChangeHandler = (status: boolean) => {
  if (status) {
    appBus.emit('app-hidden')

    return
  }
  scheduleAppVisible()
}

appBus.on((event) => {
  switch (event.type) {
    case 'order-modal-dismiss':
      return onOrderModalDismiss()
    case 'order-tap':
      const order = event.payload
      orderModalIsOpen.value = true
      orderModalActiveOrderId.value = orderModalIsOpen.value ? order._id : null

      return
    case 'table-tap':
      const { object } = event.payload
      orderModalIsOpen.value = true
      orderModalActiveObjectId.value = object.object._id
      orderModalActiveOrderId.value = null

      return
    case 'app-visible':
      return reloadStoredEntities()
  }
})

const onOrderModalDismiss = () => {
  orderModalIsOpen.value = false
  orderModalActiveOrderId.value = null
  orderModalActiveObjectId.value = null
}

const onOrderModalUpdate: ExtractEmits<typeof OrderModalLayout>['update'] = ({
  orderId,
  objectId,
}) => {
  if (orderId) {
    orderModalActiveOrderId.value = orderId
  }

  if (objectId) {
    orderModalActiveObjectId.value = objectId
  }
}

useProvideAppUIState()
useProvideMoveItemsState()

HelpersVisibilityAddEventListener(onVisibilityChangeHandler)

let onSWRegisteredCallback:
  | ((r: ServiceWorkerRegistration | undefined) => void)
  | undefined

const { needRefresh, updateServiceWorker } = useRegisterSW({
  onRegisteredSW(_swUrl, registration) {
    onSWRegisteredCallback?.(registration)
  },
})

const appUpdate = useAppUpdate({
  packageVersion: packageJson.version,
  needRefresh,
  updateServiceWorker,
  registerOnSWHook: (cb) => {
    onSWRegisteredCallback = cb
  },
})

// Provide update API to descendants (User page reads it for the
// "Check for updates" button without re-registering the SW).
provide('appUpdate', appUpdate)

// Toast shown when a new SW is waiting. Stays visible (no duration)
// until user reloads or dismisses — we don't want them to miss it.
let updateToast: HTMLIonToastElement | null = null

const presentUpdateToast = async () => {
  if (updateToast) return

  const latest = appUpdate.latestVersion.value
  const message = latest
    ? t('update.toastMessage', {
        current: appUpdate.currentVersion,
        latest,
      })
    : t('update.toastMessageFallback')

  updateToast = await toastController.create({
    message,
    position: 'top',
    cssClass: 'toast text-md-4-medium',
    buttons: [
      {
        text: t('update.reload'),
        role: 'confirm',
        handler: () => {
          appUpdate.applyUpdate()
        },
      },
      {
        text: t('common.close'),
        role: 'cancel',
      },
    ],
  })
  updateToast.addEventListener('didDismiss', () => {
    updateToast = null
  })
  await updateToast.present()
}

const appColorScheme = usePreferredColorScheme()

const authInProgress = ref(false)
const loginModalIsOpen = ref(false)
const shiftModalIsOpen = ref(false)

const orderModalIsOpen = ref(false)
const orderModalActiveOrderId = ref<string | null>(null)
const orderModalActiveObjectId = ref<string | null>(null)
const rootElementRef = ref<InstanceType<typeof IonRouterOutlet> | null>(null)

const mapIsEnabled = computed(() => {
  return AppStore.selectedStorefront.inHouseService.setup.map
})

const tabsIsEnabled = computed(() => {
  return AppStore.selectedStorefront.inHouseService.setup.tabs
})

const onLogin = () => {
  loginModalIsOpen.value = false
  AuthStore.lastSessionLogin = Date.now()

  router.replace({
    name: mapIsEnabled.value ? 'map' : tabsIsEnabled.value ? 'tabs' : 'user',
  })
}

const authenticate = async (data = {}) => {
  const urlToken = null
  const token = window.localStorage.getItem('accessToken') || urlToken

  if (token) {
    authInProgress.value = true

    await AuthStore.authenticate({
      strategy: 'jwt',
      accessToken: token,
      ...data,
    })

    authInProgress.value = false
  }
}

const reloadStoredEntities = () => {
  return HelpersAppReloadStoredEntities(AuthStore, AppsStore).catch(
    async (error) => {
      // NotAuthenticated during a reload burst is almost always the
      // wake-from-sleep race with the socket's own jwt-reauth. The
      // reauth completes and `io.on('connect')` will fire another
      // `app-visible`, retrying the whole set — user has nothing to
      // act on, just noise. `scheduleAppVisible` upstream avoids
      // most of these; this is defense in depth.
      if (error?.name === 'NotAuthenticated') return

      const toast = await toastController.create({
        message: `Error: ${error.name}. ${error.message}`,
        duration: 3000,
        cssClass: 'toast text-lg-7-semibold',
        position: 'bottom',
        mode: 'ios',
      })

      await toast.present()
    },
  )
}

watch(
  computed(() => appColorScheme.value),
  (newValue) => {
    if (!Capacitor.isNativePlatform()) return

    if (newValue === 'light') {
      StatusBar.setStyle({ style: Style.Light })
    } else {
      StatusBar.setStyle({ style: Style.Dark })
    }
  },
  { immediate: true },
)

watch(
  computed(() => AuthStore.reactiveUser),
  (newValue) => {
    const locale = newValue?.profile?.preferredAppLanguage
    if (locale) AppStore.setLocale(locale as 'en-gb' | 'bg' | 'ru')
  },
  { immediate: true },
)

watch(
  computed(() => AuthStore.isUserAuthenticated),
  (newValue) => {
    if (!newValue) {
      router.replace({ name: 'unauthorized' })
      loginModalIsOpen.value = true
    }
  },
)

watch(
  computed(() => {
    return (
      route.fullPath ||
      shift.value ||
      shiftIsHappening.value ||
      shiftShouldBeStarted.value ||
      shiftsSupported.value
    )
  }),
  () => {
    if (route.name === 'user' || String(route.name).includes('user')) return

    if (shift.value && shiftShouldBeStarted.value && shiftsSupported.value) {
      shiftModalIsOpen.value = true
    } else {
      shiftModalIsOpen.value = false
    }
  },
  {
    immediate: true,
  },
)

watch(
  computed(() => appUpdate.isUpdateAvailable.value),
  (isAvailable) => {
    if (isAvailable) presentUpdateToast()
  },
  { immediate: true },
)

watch(
  computed(() => needRefresh.value),
  () => appUpdate.watchSWUpdate(),
  { immediate: true },
)

onMounted(() => {
  return authenticate()
    .then(() => {
      if (!AuthStore.isUserAuthenticated) {
        loginModalIsOpen.value = true
      } else {
        onLogin()
      }
    })
})

onDeactivated(() =>
  HelpersVisibilityRemoveEventListener(onVisibilityChangeHandler),
)
</script>
